'use client';

import type { Dayjs } from 'dayjs';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';

import { RouterLink } from 'src/routes/components';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomPopover } from 'src/components/custom-popover';

import { useAttGöra } from 'src/sections/att-gora/use-att-gora';
import { SVENSKA_MÅNADER } from 'src/sections/moms/momsPeriod';
import {
  brådska,
  BRÅDSKA_FÄRG,
  beskrivDagarKvar,
  MOMSPERIOD_ETIKETT,
  UPPGIFTSTYP_ETIKETT,
} from 'src/sections/att-gora/AttGoraTyper';

import type { MomsperiodVal, AttGöraPost } from 'src/sections/att-gora/AttGoraTyper';
import type { UppgiftsTyp } from 'src/sections/att-gora/myndighetsdatum';

// ----------------------------------------------------------------------

const mediaQuery = 'md';

const kortMånad = (månadsIndex: number) => SVENSKA_MÅNADER[månadsIndex].slice(0, 3);

type Grupp = { rubrik: string; poster: AttGöraPost[] };

function gruppera(poster: AttGöraPost[], idag: Dayjs): Grupp[] {
  const försenade: AttGöraPost[] = [];
  const inomTrettio: AttGöraPost[] = [];
  const senare: AttGöraPost[] = [];

  poster.forEach((post) => {
    const dagar = post.förfallodag.startOf('day').diff(idag.startOf('day'), 'day');
    if (dagar < 0) försenade.push(post);
    else if (dagar <= 30) inomTrettio.push(post);
    else senare.push(post);
  });

  return [
    { rubrik: 'Försenat', poster: försenade },
    { rubrik: 'Inom 30 dagar', poster: inomTrettio },
    { rubrik: 'Längre fram', poster: senare },
  ].filter((grupp) => grupp.poster.length > 0);
}

// ----------------------------------------------------------------------

/**
 * Hur länge en nyss avbockad rad blir kvar i listan innan den flyttas ned till
 * de klara. Bocken och överstrykningen ska hinna synas — försvinner raden i
 * samma bildruta som klicket är det inte längre uppenbart vad som hände.
 */
const KVAR_EFTER_AVBOCKNING_MS = 900;

function useFördröjdBortplockning() {
  const [kvarhållna, setKvarhållna] = useState<string[]>([]);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const pågående = timers.current;
    return () => Object.values(pågående).forEach(clearTimeout);
  }, []);

  const släpp = useCallback((id: string) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setKvarhållna((tidigare) => tidigare.filter((k) => k !== id));
  }, []);

  const håll = useCallback(
    (id: string) => {
      clearTimeout(timers.current[id]);
      setKvarhållna((tidigare) => (tidigare.includes(id) ? tidigare : [...tidigare, id]));
      timers.current[id] = setTimeout(() => släpp(id), KVAR_EFTER_AVBOCKNING_MS);
    },
    [släpp]
  );

  return { kvarhållna, håll, släpp };
}

// ----------------------------------------------------------------------

export type AttGöraPopoverProps = ButtonBaseProps;

export function AttGöraPopover({ sx, ...other }: AttGöraPopoverProps) {
  const { open, anchorEl, onClose, onOpen } = usePopover();
  const [visaInställningar, setVisaInställningar] = useState(false);
  const [visaKlara, setVisaKlara] = useState(false);
  const { kvarhållna, håll, släpp } = useFördröjdBortplockning();

  const {
    idag,
    öppna,
    nästa,
    poster,
    ärKlar,
    uppdatera,
    avklaratAutomatiskt,
    antalFörsenade,
    inställningar,
    växlaKlar,
    växlaTyp,
    uppdateraInställningar,
    momsperiod,
    ärArbetsgivare,
    arbetsgivarbedömning,
    ärMomsregistrerad,
    ärPrivatperson,
    harRäkenskapsår,
  } = useAttGöra();

  /**
   * Avbockade poster lämnar listan av sig själva — de som appen härlett som
   * gjorda (bokförda, inlämnade, betalda) direkt, och handkryssade efter en
   * kort stund så att bocken hinner synas. De samlas i stället under "Klara",
   * där en handkryssad post kan ångras.
   */
  const öppnaIListan = useMemo(
    () => poster.filter((post) => !ärKlar(post.id) || kvarhållna.includes(post.id)),
    [poster, ärKlar, kvarhållna]
  );

  const klaraPoster = useMemo(
    () => poster.filter((post) => ärKlar(post.id) && !kvarhållna.includes(post.id)),
    [poster, ärKlar, kvarhållna]
  );

  const grupper = useMemo(() => gruppera(öppnaIListan, idag), [öppnaIListan, idag]);

  const hanteraVäxlaKlar = (id: string) => {
    if (ärKlar(id)) släpp(id);
    else håll(id);
    växlaKlar(id);
  };

  const nivå = nästa ? brådska(nästa.förfallodag, idag) : 'kommande';
  const färg = antalFörsenade > 0 ? 'error' : BRÅDSKA_FÄRG[nivå];

  /** Hämtar om bokförings-status så att något nyss bokfört syns utan omladdning. */
  const öppna_panel = (event: React.MouseEvent<HTMLElement>) => {
    uppdatera();
    onOpen(event);
  };

  const renderKnapp = () => (
    <ButtonBase
      disableRipple
      onClick={öppna_panel}
      aria-label={
        nästa
          ? `Att göra: ${nästa.titel}, ${beskrivDagarKvar(nästa.förfallodag, idag)}`
          : 'Att göra'
      }
      sx={[
        (theme) => ({
          px: 1.25,
          py: 0.75,
          gap: 1,
          minWidth: 0,
          maxWidth: 420,
          borderRadius: 1.5,
          border: `1px solid ${theme.vars ? `rgba(${theme.vars.palette.grey['500Channel']} / 0.16)` : theme.palette.divider}`,
          transition: theme.transitions.create(['background-color', 'border-color']),
          '&:hover': { bgcolor: 'action.hover' },
          ...(färg === 'error' && { borderColor: 'error.main' }),
          ...(färg === 'warning' && { borderColor: 'warning.main' }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Iconify
        width={18}
        icon={antalFörsenade > 0 ? 'solar:danger-triangle-bold-duotone' : 'solar:calendar-mark-bold-duotone'}
        sx={{
          flexShrink: 0,
          color:
            färg === 'error' ? 'error.main' : färg === 'warning' ? 'warning.main' : 'text.secondary',
        }}
      />

      <Box sx={{ minWidth: 0, textAlign: 'left', display: { xs: 'none', [mediaQuery]: 'block' } }}>
        <Typography
          variant="caption"
          sx={{ display: 'block', lineHeight: 1.2, color: 'text.disabled' }}
        >
          Att göra
        </Typography>
        <Typography
          noWrap
          variant="subtitle2"
          sx={{ display: 'block', lineHeight: 1.3, maxWidth: 260 }}
        >
          {nästa ? nästa.titel : '-'}
        </Typography>
      </Box>

      {nästa && (
        <Chip
          size="small"
          variant="soft"
          color={färg === 'default' ? undefined : färg}
          label={beskrivDagarKvar(nästa.förfallodag, idag)}
          sx={{ flexShrink: 0, height: 22, fontWeight: 'fontWeightSemiBold' }}
        />
      )}

      {öppna.length > 1 && (
        <Typography
          variant="caption"
          sx={{ flexShrink: 0, color: 'text.disabled', display: { xs: 'none', [mediaQuery]: 'block' } }}
        >
          +{öppna.length - 1}
        </Typography>
      )}

      <Iconify width={16} icon="carbon:chevron-sort" sx={{ flexShrink: 0, color: 'text.disabled' }} />
    </ButtonBase>
  );

  const renderRad = (post: AttGöraPost) => {
    /** Satt när posten är avklarad av appens egen data — bokförd, inlämnad, betald. */
    const automatiskAnledning = avklaratAutomatiskt[post.id];
    const klar = ärKlar(post.id);
    const nivåFörPost = brådska(post.förfallodag, idag);
    const postFärg = BRÅDSKA_FÄRG[nivåFörPost];

    return (
      <Box
        key={post.id}
        sx={{
          px: 2,
          py: 1.25,
          gap: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          opacity: klar ? 0.45 : 1,
          transition: (theme) => theme.transitions.create('opacity'),
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {/* Datumrälsen till vänster gör listan läsbar som en kalender. */}
        <Box
          sx={{
            width: 40,
            flexShrink: 0,
            textAlign: 'center',
            borderRadius: 1,
            py: 0.5,
            bgcolor: 'background.neutral',
          }}
        >
          <Typography variant="subtitle2" sx={{ lineHeight: 1.1 }}>
            {post.förfallodag.date()}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase' }}>
            {kortMånad(post.förfallodag.month())}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'fontWeightMedium',
              textDecoration: klar ? 'line-through' : 'none',
            }}
          >
            {post.titel}
          </Typography>

          <Box sx={{ mt: 0.5, gap: 0.75, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            {automatiskAnledning ? (
              <Chip
                size="small"
                variant="soft"
                color="success"
                icon={<Iconify width={13} icon="solar:check-circle-bold" />}
                label={automatiskAnledning}
                sx={{ height: 20, fontSize: 11, '& .MuiChip-icon': { ml: 0.5 } }}
              />
            ) : (
              <Chip
                size="small"
                variant="soft"
                color={postFärg === 'default' ? undefined : postFärg}
                label={beskrivDagarKvar(post.förfallodag, idag)}
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {post.avsändare}
              {post.lagrum ? ` · ${post.lagrum}` : ''}
            </Typography>
          </Box>

          {post.detalj && (
            <Typography variant="caption" sx={{ mt: 0.25, display: 'block', color: 'text.secondary' }}>
              {post.detalj}
            </Typography>
          )}

          {/*
            Länken har funnits på posten sedan `tillAttGöraPost` skrevs, men renderades aldrig.
            Den behövs för uppgifter där appen har en egen sida som hjälper till — utan den går
            fastighetsdeklarationsguiden inte att nå härifrån.
          */}
          {post.länk && (
            <Link
              {...(post.internLänk
                ? { component: RouterLink, href: post.länk, onClick: onClose }
                : { href: post.länk, target: '_blank', rel: 'noopener' })}
              variant="caption"
              sx={{ mt: 0.5, gap: 0.25, display: 'inline-flex', alignItems: 'center' }}
            >
              {post.länkText ?? `Till ${post.avsändare}`}
              <Iconify
                width={13}
                icon={post.internLänk ? 'eva:arrow-ios-forward-fill' : 'solar:arrow-right-up-linear'}
              />
            </Link>
          )}
        </Box>

        {/* Härledd avbockning går inte att kryssa bort — den speglar bokföringen. */}
        <Tooltip
          title={
            automatiskAnledning
              ? `${automatiskAnledning} enligt bokföringen`
              : klar
                ? 'Markera som ej klar'
                : 'Markera som klar'
          }
        >
          <span>
            <Checkbox
              size="small"
              checked={klar}
              disabled={Boolean(automatiskAnledning)}
              onChange={() => hanteraVäxlaKlar(post.id)}
              icon={<Iconify icon="solar:check-circle-linear" />}
              checkedIcon={<Iconify icon="solar:check-circle-bold" />}
              sx={{ p: 0.5, mt: -0.25 }}
            />
          </span>
        </Tooltip>
      </Box>
    );
  };

  /** Raden krymper ihop när den bockats av, i stället för att bara klippas bort. */
  const renderÖppenRad = (post: AttGöraPost) => (
    <Collapse key={post.id} in={!ärKlar(post.id)} timeout={320}>
      {renderRad(post)}
    </Collapse>
  );

  const renderKlara = () => (
    <Box>
      <ButtonBase
        onClick={() => setVisaKlara((visas) => !visas)}
        aria-expanded={visaKlara}
        sx={{
          px: 2,
          py: 1,
          gap: 1,
          width: 1,
          justifyContent: 'flex-start',
          color: 'text.disabled',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Iconify width={16} icon="solar:check-circle-bold" sx={{ color: 'success.main' }} />
        <Typography variant="overline" sx={{ flexGrow: 1, textAlign: 'left' }}>
          Klara ({klaraPoster.length})
        </Typography>
        <Iconify
          width={16}
          icon={visaKlara ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
        />
      </ButtonBase>
      <Collapse in={visaKlara} unmountOnExit>
        {klaraPoster.map(renderRad)}
      </Collapse>
    </Box>
  );

  const renderMomsperiod = () => (
    <>
      <Typography variant="overline" sx={{ display: 'block', color: 'text.disabled' }}>
        Momsredovisning
      </Typography>
      <Box sx={{ mt: 0.75, gap: 0.75, display: 'flex', flexWrap: 'wrap' }}>
        {(Object.keys(MOMSPERIOD_ETIKETT) as MomsperiodVal[]).map((val) => {
          const aktivt = val === 'INGEN' ? !momsperiod : momsperiod === val;
          return (
            <Chip
              key={val}
              size="small"
              clickable
              label={MOMSPERIOD_ETIKETT[val]}
              aria-label={`Momsredovisning: ${MOMSPERIOD_ETIKETT[val]}`}
              aria-pressed={aktivt}
              onClick={() => uppdateraInställningar({ momsperiod: val })}
              variant={aktivt ? 'soft' : 'outlined'}
              color={aktivt ? 'info' : undefined}
              sx={{ height: 24 }}
            />
          );
        })}
      </Box>
      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.disabled' }}>
        Styr momsdeklarationernas datum. Hämtas från organisationen om den har ett värde.
      </Typography>
    </>
  );

  const renderInställningar = () => (
    <Box sx={{ px: 2, py: 1.5 }}>
      {renderMomsperiod()}

      <Typography variant="overline" sx={{ mt: 1.5, display: 'block', color: 'text.disabled' }}>
        Gäller för organisationen
      </Typography>

      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column' }}>
        {!ärPrivatperson && (
          <Box>
            <FormControlLabel
              label={<Typography variant="body2">Registrerad som arbetsgivare</Typography>}
              control={
                <Switch
                  size="small"
                  checked={ärArbetsgivare}
                  onChange={(e) => uppdateraInställningar({ ärArbetsgivareVal: e.target.checked })}
                />
              }
            />
            <Typography
              variant="caption"
              sx={{ ml: 1.75, display: 'block', color: 'text.disabled' }}
            >
              {inställningar.ärArbetsgivareVal === undefined
                ? arbetsgivarbedömning.anledning
                : 'Eget val. Återställ för att låta organisationens uppgifter avgöra.'}
            </Typography>
            {inställningar.ärArbetsgivareVal !== undefined && (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => uppdateraInställningar({ ärArbetsgivareVal: undefined })}
                sx={{ ml: 1.75, mt: 0.25, display: 'block' }}
              >
                Återställ
              </Link>
            )}
          </Box>
        )}
        <FormControlLabel
          label={<Typography variant="body2">Betalar debiterad F-skatt</Typography>}
          control={
            <Switch
              size="small"
              checked={inställningar.harDebiteradPreliminärskatt}
              onChange={(e) =>
                uppdateraInställningar({ harDebiteradPreliminärskatt: e.target.checked })
              }
            />
          }
        />
        <FormControlLabel
          label={<Typography variant="body2">Lämnar deklarationer digitalt</Typography>}
          control={
            <Switch
              size="small"
              checked={inställningar.lämnarDigitalt}
              onChange={(e) => uppdateraInställningar({ lämnarDigitalt: e.target.checked })}
            />
          }
        />
      </Box>

      <Typography variant="overline" sx={{ mt: 1, display: 'block', color: 'text.disabled' }}>
        Visa
      </Typography>
      <Box sx={{ mt: 0.75, gap: 0.75, display: 'flex', flexWrap: 'wrap' }}>
        {(Object.keys(UPPGIFTSTYP_ETIKETT) as UppgiftsTyp[]).map((typ) => {
          const dold = inställningar.dolda.includes(typ);
          return (
            <Chip
              key={typ}
              size="small"
              clickable
              onClick={() => växlaTyp(typ)}
              label={UPPGIFTSTYP_ETIKETT[typ]}
              aria-label={`${dold ? 'Visa' : 'Dölj'} ${UPPGIFTSTYP_ETIKETT[typ]}`}
              aria-pressed={!dold}
              variant={dold ? 'outlined' : 'soft'}
              color={dold ? undefined : 'success'}
              sx={{ height: 24, opacity: dold ? 0.5 : 1 }}
            />
          );
        })}
      </Box>
    </Box>
  );

  const renderPopover = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{ arrow: { placement: 'top-center' }, paper: { sx: { mt: 0.5, width: 400 } } }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Att göra
        </Typography>
        {antalFörsenade > 0 && (
          <Chip size="small" variant="soft" color="error" label={`${antalFörsenade} försenade`} />
        )}
        <Tooltip title="Inställningar">
          <IconButton
            size="small"
            aria-label="Inställningar för att göra"
            onClick={() => setVisaInställningar((visas) => !visas)}
            sx={{ color: visaInställningar ? 'primary.main' : 'text.disabled' }}
          >
            <Iconify width={18} icon="solar:settings-bold-duotone" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="caption" sx={{ px: 2, pb: 1, display: 'block', color: 'text.disabled' }}>
        Datumen räknas ut från räkenskapsåret och momsperioden. Helg och helgdag är framflyttade.
      </Typography>

      <Divider sx={{ borderStyle: 'dashed' }} />

      {!harRäkenskapsår && (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Organisationen saknar registrerat räkenskapsår. Lägg till ett för att få
            inkomstdeklaration, stämma och årsredovisning i listan.
          </Typography>
        </Box>
      )}

      {!momsperiod && !ärPrivatperson && ärMomsregistrerad && !inställningar.dolda.includes('MOMSDEKLARATION') && (
        <Box
          sx={{ px: 2, py: 1.25, gap: 1, display: 'flex', alignItems: 'center', bgcolor: 'background.neutral' }}
        >
          <Iconify width={18} icon="solar:info-circle-bold-duotone" sx={{ color: 'info.main', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ flexGrow: 1, color: 'text.secondary' }}>
            Ingen momsperiod är registrerad, så momsdeklarationerna saknas i listan.
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setVisaInställningar(true)}>
            Välj
          </Button>
        </Box>
      )}

      {poster.length === 0 ? (
        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Inga uppgifter inom horisonten.
          </Typography>
        </Box>
      ) : (
        <Scrollbar sx={{ maxHeight: 360 }}>
          {grupper.map((grupp) => (
            <Box key={grupp.rubrik}>
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                  display: 'block',
                  color: grupp.rubrik === 'Försenat' ? 'error.main' : 'text.disabled',
                }}
              >
                {grupp.rubrik}
              </Typography>
              {grupp.poster.map(renderÖppenRad)}
            </Box>
          ))}

          {öppnaIListan.length === 0 && (
            <Box sx={{ px: 2, py: 3, gap: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Iconify width={28} icon="solar:check-circle-bold-duotone" sx={{ color: 'success.main' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Allt är avbockat. Inget väntar just nu.
              </Typography>
            </Box>
          )}

          {klaraPoster.length > 0 && renderKlara()}
        </Scrollbar>
      )}

      <Divider sx={{ borderStyle: 'dashed' }} />

      <Collapse in={visaInställningar} unmountOnExit>
        {renderInställningar()}
        <Divider sx={{ borderStyle: 'dashed' }} />
      </Collapse>

      <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 2 }}>
        <Link
          href="https://www.skatteverket.se/foretag"
          target="_blank"
          rel="noopener"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          Skatteverket
          <Iconify width={14} icon="solar:arrow-right-up-linear" />
        </Link>
        <Link
          href="https://bolagsverket.se/"
          target="_blank"
          rel="noopener"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          Bolagsverket
          <Iconify width={14} icon="solar:arrow-right-up-linear" />
        </Link>
      </Box>
    </CustomPopover>
  );

  return (
    <>
      {renderKnapp()}
      {renderPopover()}
    </>
  );
}
